import * as React from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Link from '@mui/material/Link'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import styles from '@styles/signup.module.css'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import Container from '@mui/material/Container'
import { useState } from 'react'
import { useRouter } from 'next/router'
import TextFields from '@components/form/TextFields'
import { SigninButton } from '@components/form/Buttons'
import Image from 'next/image'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import routes from '@config/routes'
import { postSignUp } from '@api/auth'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { useTranslation } from '@contexts/translation'
import { useTheme } from '@mui/material/styles'
import { responseHandler } from '@utils/responseHandler'

const TERMS_OF_SERVICE_URL = 'https://keysy.co/tos/'
const PRIVACY_POLICY_URL = 'https://keysy.co/privacy/'

const defaultTheme = createTheme()

export default function SignUpSide() {
  const router = useRouter()
  const [policiesAccepted, setPoliciesAccepted] = useState(false)
  const { dictionary, lang } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState(false)

  const schema = yup.object().shape({
    first_name: yup.string().required(dictionary.required),
    last_name: yup.string().required(dictionary.required),
    username: yup.string().email().required(dictionary.required),
    password: yup
      .string()
      .min(8, dictionary.errors_password_minLength)
      .test('isValid', dictionary.errors_password_pattern, value => {
        const hasAlpha = /[aA-zZ]/.test(value)
        const hasNum = /[0-9]/.test(value)
        if (hasAlpha && hasNum) return true
        return false
      })
      .required(dictionary.required),
    password_again: yup
      .string()
      .oneOf([yup.ref('password'), null], dictionary.errors_password_not_match)
      .required(dictionary.required)
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    setError
  } = useForm({
    resolver: yupResolver(schema)
  })

  const onSubmit = async values => {
    const payload = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.username,
      username: values.username,
      password: values.password,
      locale: lang
    }

    setIsLoading(true)
    const response = await postSignUp(payload)

    responseHandler({
      response,
      callback: async () => {
        router.push({
          pathname: routes.account.registration,
          query: { email: values.username }
        })
      },
      dictionary,
      snackbar: enqueueSnackbar,
      setError
    })
    setIsLoading(response.data.errors ? false : true)
  }

  const handleCheckboxChange = e => {
    setPoliciesAccepted(e.target.checked)
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Grid container component="main" sx={{ height: '100vh' }}>
        <CssBaseline />
        <Grid
          className={styles.leftsection}
          item
          xs={false}
          sm={4}
          md={6}
          sx={{
            backgroundColor: t =>
              t.palette.mode === 'light'
                ? t.palette.grey[50]
                : t.palette.grey[900]
          }}
        >
          <Image
            className={styles.bgimg}
            src="/default_blog_post.jpeg"
            width={1900}
            height={1267}
            loading="eager"
            layout="fill"
            alt=""
          />

          <div style={{ width: '100%', textAlign: 'center' }}>
            <Image
              className={styles.logo}
              src="/keysy_logo_light.png"
              width={245}
              height={70}
              alt=""
            />
          </div>

          <p className={styles.content} style={{ textAlign: 'center' }}>
            {dictionary.keysy_tag_line}
          </p>
        </Grid>

        <Container component="main" maxWidth="xs">
          <Box
            className={styles.rightsection}
            sx={{
              marginTop: 20,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography
              component="h1"
              variant="h5"
              className={styles.h1}
              data-testid="Typography-4b73f0aa-14e9-44bc-b4f9-549086d6a5a0"
            >
              <strong> {dictionary.signup}</strong>
            </Typography>
            <Grid container justifyContent="flex-center">
              <Grid item>
                {dictionary.already_have_an_account}{' '}
                <Link href={routes.account.login}>{dictionary.login}</Link>
              </Grid>
            </Grid>
            <Box
              component="form"
              className={styles.primaryform}
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              sx={{ mt: 2 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextFields
                    id="first_name"
                    label={dictionary.first_name}
                    name="first_name"
                    autoComplete="first_name"
                    errorMsg={errors.first_name?.message}
                    control={control}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextFields
                    id="last_name"
                    label={dictionary.last_name}
                    name="last_name"
                    autoComplete="last_name"
                    errorMsg={errors.last_name?.message}
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFields
                    id="username"
                    label={dictionary.email}
                    name="username"
                    autoComplete="email"
                    errorMsg={errors.username?.message}
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFields
                    id="password"
                    label={dictionary.password}
                    name="password"
                    autoComplete="password"
                    errorMsg={errors.password?.message}
                    type="password"
                    control={control}
                  ></TextFields>
                </Grid>

                <Grid item xs={12}>
                  <TextFields
                    id="password_again"
                    type="password"
                    label={dictionary.password_again}
                    // value={confirmpassword}
                    name="password_again"
                    autoComplete="password_again"
                    errorMsg={errors.password_again?.message}
                    control={control}
                  ></TextFields>
                </Grid>
                <Grid className="agreetermsconditions" item xs={12}>
                  <FormControlLabel
                    className={styles.label}
                    control={
                      <Checkbox
                        onChange={handleCheckboxChange}
                        value="allowExtraEmails"
                        sx={{
                          '&.Mui-checked': {
                            color: theme.palette.primary.main
                          }
                        }}
                        data-testid="Checkbox-5b338f46-258d-410b-bcb0-7d96db241032"
                      />
                    }
                  />

                  <span data-testid="span-106f17d8-df5a-441f-8bff-233ea97c28c2">
                    {dictionary.read_accepted}{' '}
                    <Link href={TERMS_OF_SERVICE_URL}>
                      {dictionary.the_terms_of_service}
                    </Link>{' '}
                    {dictionary.and}{' '}
                    <Link href={PRIVACY_POLICY_URL}>
                      {dictionary.privacy_policy}
                    </Link>
                  </span>
                </Grid>
              </Grid>

              <SigninButton
                label={dictionary.signup}
                className={styles.signinbtn}
                type="submit"
                disabled={!policiesAccepted || isLoading}
              />
            </Box>
          </Box>
        </Container>
      </Grid>
    </ThemeProvider>
  )
}
